/**
 * The two errors that mean "this route starts its own interaction", not "something failed".
 *
 * A route mounted with `InteractionType.None` still runs msal-react's acquire effect, and that
 * effect has nowhere to go: with no account it calls `login()`, whose switch has no case for None
 * and falls to `default:`, which is `invalid_interaction_type`. An authenticated visitor whose
 * silent token needs interaction lands in the same place, because the fallback interaction type is
 * also None. So on a self-directed route this error is not a failure - it is the hook reporting
 * that it was asked to do nothing, which is exactly what was wanted.
 *
 * `unable_to_fallback_to_interaction` is the same kind of answer: an interaction is already in
 * progress, which on these routes is usually the page's own redirect. Signing somebody out in the
 * middle of it is the loop this guard exists to prevent.
 *
 * Codes rather than `instanceof`: msal-react does not export `ReactAuthError` from its public
 * entry point, and these two strings are its documented identities.
 */
export const SELF_DIRECTED_EXPECTED_ERRORS = ['invalid_interaction_type', 'unable_to_fallback_to_interaction'];

/**
 * Whether an MSAL error on a self-directed route is the expected outcome rather than a fault.
 *
 * Deliberately a whitelist of two. Everything else - a server error, a network failure, a cache or
 * crypto fault from `acquireTokenSilent` on a visitor who IS signed in - is a real failure and
 * must keep reaching the handler it would reach on any other route. Exempting the whole route from
 * error handling, which is what the first version of this did, would have turned those into a page
 * that sits there with nothing said.
 * @param err - The error msal-react reported, if any.
 * @returns True when it means "nothing to do here", false for a genuine failure.
 */
export const isExpectedSelfDirectedError = (err: unknown): boolean => {
  const code = (err as { errorCode?: string } | null | undefined)?.errorCode;
  return !!code && SELF_DIRECTED_EXPECTED_ERRORS.includes(code);
};
